import { clearEntitlements } from './entitlements';

// API_BASE   — origin to send API calls to (default: same origin as the app)
//              Override with REACT_APP_API_BASE or REACT_APP_API_PORT.
// API_PREFIX — path prefix stripped by the reverse proxy before reaching Python.
//              Default: /api/aiservice

const _apiPort = process.env.REACT_APP_API_PORT;
const _apiBase = process.env.REACT_APP_API_BASE
  || (_apiPort
    ? `${window.location.protocol}//${window.location.hostname}:${_apiPort}`
    : window.location.origin);

const API_PREFIX = process.env.REACT_APP_API_PREFIX ?? '/api/aiservice';

export const STREAM_API = `${_apiBase}${API_PREFIX}`;

// ─── Token acquisition ────────────────────────────────────────────────────────

const _TOKEN_KEY = 'prvsk_token';
// Login state (authSlice) — the token must share its lifetime, see below.
const _AUTH_KEY  = 'eu_auth';
const _RESET_KEY = 'eu_reset_required';

// The token lives in localStorage, NOT sessionStorage, so it has the same scope
// and lifetime as the login state in `eu_auth`. They used to disagree: a new
// browser tab inherited eu_auth (localStorage) but started with no token
// (sessionStorage is per-tab), fell through to the device-pairing flow below,
// and ended up authenticated as the DEVICE (`admin_ParvekshakUI`) rather than
// the logged-in web user. The backend resolves that subject to the built-in
// `enduser` role, so the tab silently ran with the wrong identity — visible as a
// shrunken sidebar — until the user logged out and back in.
let _token = localStorage.getItem(_TOKEN_KEY) || sessionStorage.getItem(_TOKEN_KEY) || null;
// Carry over a token left in sessionStorage by an older build so tabs open at
// the time of the update aren't bounced to /login.
if (_token) { localStorage.setItem(_TOKEN_KEY, _token); sessionStorage.removeItem(_TOKEN_KEY); }

// Hands the JWT to the native Flutter shell so it can register this device's
// FCM token (PushService.registerWithJwt) — without this, mobile app sessions
// never register for push/SOS and only the web session receives them.
function _notifyFlutterAuth(t) {
  if (t && typeof window !== 'undefined' && window.FlutterAuth) {
    window.FlutterAuth.postMessage(t);
  }
}
_notifyFlutterAuth(_token);

export function setToken(t) {
  _token = t || null;
  if (_token) localStorage.setItem(_TOKEN_KEY, _token);
  else        localStorage.removeItem(_TOKEN_KEY);
  _notifyFlutterAuth(_token);
}

export function resetSession() {
  _token = null;
  localStorage.removeItem(_TOKEN_KEY);
  sessionStorage.removeItem(_TOKEN_KEY);   // legacy key from older builds
  clearEntitlements();
}

// The token as currently held — no network, no pairing. For callers that only
// want to read the JWT payload (e.g. the client id in the navbar).
export function peekToken() { return _token; }

// client_id stored by the login page — needed for router to route pairing requests.
const _clientId = () => localStorage.getItem('prvsk_client_id') || process.env.REACT_APP_CLIENT_ID || '';

async function _fetchToken() {
  // Login is mandatory — auto-pairing via /auth/pairing-code is disabled.
  // Kept for reference: this flow silently paired as device identity (admin_ParvekshakUI)
  // when no eu_auth was present, causing permission mismatches in multi-user setups.
  // Re-enable only if unauthenticated local-device access is needed again.
  //
  // const cid = _clientId();
  // const qs  = cid ? `?client_id=${encodeURIComponent(cid)}` : '';
  // const r1 = await fetch(`${STREAM_API}/auth/pairing-code${qs}`);
  // if (!r1.ok) return null;
  // const { pairing_code } = await r1.json();
  // if (!pairing_code) return null;
  // const r2 = await fetch(`${STREAM_API}/auth/pair${qs}`, {
  //   method:  'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body:    JSON.stringify({ code: pairing_code, device_name: 'ParvekshakUI' }),
  // });
  // if (!r2.ok) return null;
  // const d = await r2.json();
  // return d.token || d.access_token || d.data?.token || null;
  return null;
}

// Send a session that has lost its token back to the login screen. Clearing the
// auth keys keeps "no token" and "not logged in" the same state, so the app
// shell doesn't render half-authenticated on the way out.
function _toLogin() {
  localStorage.removeItem(_AUTH_KEY);
  localStorage.removeItem(_RESET_KEY);
  if (window.location.pathname !== '/login') window.location.href = '/login';
}

export async function getToken() {
  if (_token) return _token;
  // A logged-in web session with no token must re-authenticate AS ITSELF.
  // Pairing would mint a device token under a different subject, and every
  // later permission check would be evaluated against the wrong user — fail
  // loudly instead. Tokenless use (no web login at all) keeps the pairing path.
  if (localStorage.getItem(_AUTH_KEY)) { _toLogin(); return null; }
  _token = await _fetchToken();
  return _token;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

// Returns a URL with the token as ?token= query param.
// Use for <img src> / <video src> where the browser sets no headers.
export async function apiImageUrl(path) {
  const token = await getToken();
  const sep   = path.includes('?') ? '&' : '?';
  return token ? `${STREAM_API}${path}${sep}token=${token}` : `${STREAM_API}${path}`;
}

// Authenticated fetch. Injects Authorization: Bearer header automatically.
export const API_UNREACHABLE = 'prvsk:api-unreachable';
export const API_REACHABLE   = 'prvsk:api-reachable';

export async function apiFetch(path, opts = {}) {
  const token   = await getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let res;
  try {
    if (opts.json !== undefined) {
      headers['Content-Type'] = 'application/json';
      const { json, ...rest } = opts;
      res = await fetch(`${STREAM_API}${path}`, { ...rest, headers, body: JSON.stringify(json) });
    } else {
      res = await fetch(`${STREAM_API}${path}`, { ...opts, headers });
    }
  } catch (e) {
    // Network-level failure (no internet / server unreachable) — not an HTTP
    // error. navigator.onLine can't tell this apart on Wi-Fi with no uplink,
    // so OfflineLocalModeBanner listens for these instead.
    if (e?.name !== 'AbortError') window.dispatchEvent(new Event(API_UNREACHABLE));
    throw e;
  }
  window.dispatchEvent(new Event(API_REACHABLE));
  if (res.status === 401) {
    resetSession();
    window.location.href = '/login';
  }
  return res;
}
